import { createRouter, createWebHistory } from 'vue-router'

import { TenantRole } from '@/enums/tenant'
import { accessRedirect, landingRoute } from '@/libs/access'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

/*
 * What a route says about itself (docs/API.md §1.2 Permissions; `libs/access.ts` reads it).
 * `tenant` — lives under `/t/:tid/…`; `requiresAdmin` — admin rights in that tenant;
 * `requiresProvider` — a provider admin's page.
 */
declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?:     boolean
        requiresAdmin?:    boolean
        requiresProvider?: boolean
        tenant?:           boolean
        /** The closed roles that may open this tenant page too (docs/API.md §13.2, §13.6). */
        closed?:           TenantRole[]
        /** A page of the field view, the only ones a technician opens (docs/API.md §16). */
        field?:            boolean
        /**
         * The tabs (`:tab`) of a shell page a technician may open too — Naprave › Namestitev
         * (docs/PROVISIONING.md), a laptop's page rather than the phone's field view.
         */
        technicianTabs?:   string[]
        layout?:           'Default' | 'Blank' | 'Field'
    }
}

/*
 * The signed-in shell is exactly the viewport (`layouts/Default.vue`, `h-dvh`) and its `<main>`
 * is what scrolls — the window never does. So a navigation to another page scrolls THAT
 * element back to the top; a change of the tab or the query only leaves it where it is.
 */
const shellMain = (): HTMLElement | null => document.querySelector<HTMLElement>('main')

/** A tenant page: `/t/:tid/<path>`, signed in, in the shell. */
const tenantPage = (path: string, name: string, component: () => Promise<unknown>, requiresAdmin = false, closed: TenantRole[] = []) => ({
    path:      `/t/:tid/${path}`,
    name,
    component,
    meta:      { requiresAuth: true, tenant: true, requiresAdmin, closed },
})

/** A page of the field view: phone-first, no sidebar (`layouts/Field.vue`). */
const fieldPage = (path: string, name: string, component: () => Promise<unknown>) => ({
    path:      `/t/:tid/${path}`,
    name,
    component,
    meta:      { requiresAuth: true, tenant: true, field: true, layout: 'Field' as const },
})

/** Naprave (its tab is the address). */
const devicesPage = tenantPage('devices/:tab?', 'devices', () => import('@/views/device/Devices.vue'))

/** A provider admin's page. */
const providerPage = (path: string, name: string, component: () => Promise<unknown>) => ({
    path,
    name,
    component,
    meta: { requiresAuth: true, requiresProvider: true },
})

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        const main = shellMain()

        if (!main) {
            if (savedPosition)         return savedPosition
            if (from.path === to.path) return {}

            return { top: 0 }
        }

        if (from.name !== to.name) main.scrollTo({ top: 0 })

        return false
    },
    routes: [
        { path: '/', redirect: landingRoute },
        // Where a person starts: their tenant's Pregled, the provider's last tenant, or Stranke.
        {
            path:      '/start',
            name:      'landing',
            component: () => import('@/views/Landing.vue'),
            meta:      { layout: 'Blank', requiresAuth: true },
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },

        tenantPage('overview',     'overview',     () => import('@/views/overview/Overview.vue')),
        // Zahteve gostov — bookings, room-service orders, housekeeping (the closed roles' start).
        tenantPage('requests',     'requests',     () => import('@/views/request/Requests.vue'), false, [TenantRole.Kitchen, TenantRole.Housekeeping]),
        // Postrežba v sobo — the menu from Imprion POS, its settings and the POS connection.
        tenantPage('room-service', 'room-service', () => import('@/views/roomService/RoomService.vue'), false, [TenantRole.Kitchen]),
        // Pohvale, pritožbe in predlogi — guest feedback, its overview and settings (docs/API.md §18).
        tenantPage('feedback/:tab?', 'feedback',   () => import('@/views/feedback/Feedback.vue')),
        tenantPage('rooms',        'rooms',        () => import('@/views/room/Rooms.vue')),
        // Zasedenost — the room × date chart of the stays (docs/API.md §15).
        tenantPage('occupancy',    'occupancy',    () => import('@/views/occupancy/Occupancy.vue')),
        // Naprave; a technician opens it on Namestitev only (docs/PROVISIONING.md).
        { ...devicesPage, meta: { ...devicesPage.meta, technicianTabs: ['provisioning'] } },
        // TV: Kanali · Kategorije · EPG · Videz · Nastavitve TV — the tab is the address.
        tenantPage('tv/:tab?',      'tv',          () => import('@/views/tv/Tv.vue')),
        // Vsebina: Splošno · Pozdrav · Domači zaslon · Hotel · Storitve · Glasba · Program · Signage.
        tenantPage('content/:tab?', 'content',     () => import('@/views/content/Content.vue')),
        // A room-screen theme of Vsebina › Teme in the editor (a builtin opens read-only).
        tenantPage('content/themes/:id', 'content-theme', () => import('@/views/screenTheme/ScreenTheme.vue')),
        tenantPage('connector',    'connector',    () => import('@/views/connector/Connector.vue'), true),
        tenantPage('import',       'import',       () => import('@/views/import/Import.vue'), true),
        tenantPage('users',        'tenant-users', () => import('@/views/user/Users.vue'), true),
        // Milo, the AI assistant (docs/API.md §14): Nastavitve · Poraba.
        tenantPage('milo/:tab?',   'milo',         () => import('@/views/milo/Milo.vue'), true),

        // Teren — the field technicians' phone view (docs/API.md §16): the list, a device, a new screen.
        fieldPage('field',             'field',        () => import('@/views/field/Field.vue')),
        fieldPage('field/new',         'field-pair',   () => import('@/views/field/FieldPair.vue')),
        fieldPage('field/devices/:id', 'field-device', () => import('@/views/field/FieldDevice.vue')),
        // A short address to type on a phone: the field view of the person's tenant.
        {
            path:      '/teren',
            name:      'field-start',
            component: () => import('@/views/Landing.vue'),
            meta:      { layout: 'Blank', requiresAuth: true },
        },
        {
            // The pairing screen's QR (docs/API.md §4.1 `pair_url`): "Nova naprava" with the code filled in.
            path:      '/teren/nova',
            name:      'field-pair-start',
            component: () => import('@/views/Landing.vue'),
            meta:      { layout: 'Blank', requiresAuth: true },
        },

        // The provider's pages.
        providerPage('/tenants', 'tenants', () => import('@/views/tenant/Tenants.vue')),
        providerPage('/users',   'users',   () => import('@/views/user/Users.vue')),
        providerPage('/android', 'android', () => import('@/views/apk/AndroidApp.vue')),
        // The Samsung Tizen shell: its install address and the widget served there (docs/API.md §3.4).
        providerPage('/tizen',   'tizen',   () => import('@/views/tizen/TizenApp.vue')),
        // The provider's library of room-screen themes (builtins) and its editor; `new` creates.
        providerPage('/themes',     'screen-themes', () => import('@/views/screenTheme/ScreenThemes.vue')),
        providerPage('/themes/:id', 'screen-theme',  () => import('@/views/screenTheme/ScreenTheme.vue')),
        // Milo's AI and voice providers, their keys and models, and every tenant's use (§14.2).
        providerPage('/milo/:tab?', 'milo-provider', () => import('@/views/milo/MiloProvider.vue')),

        // Everybody's own account, and the provider's name for a provider admin.
        {
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:     '/:pathMatch(.*)*',
            redirect: landingRoute,
        },
    ],
})

router.beforeEach((to, _from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    // A public page never depends on who is signed in in this browser.
    if (to.meta.requiresAuth === false && to.name !== 'login') return next()

    if (loggedIn && to.name === 'login') return next(landingRoute())

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        return next({ name: 'login', query: to.fullPath !== '/' && to.name !== 'landing' ? { redirect: to.fullPath } : {} })
    }

    /*
     * A page that is not this person's. Who they are arrives with GET /me, which App.vue asks
     * for as the console starts, so on a cold load it is not known here yet — `accessRedirect`
     * then says nothing and App.vue makes the same check once the answer is there.
     */
    const redirect = accessRedirect(to)

    if (redirect) return next(redirect)

    next()
})

/*
 * The tenant of the address is the one whose role decides what may be written
 * (`stores/app.ts` → isAdmin / canEdit): set once the navigation is confirmed, before the
 * new page is drawn.
 */
router.afterEach((to) => {
    useAppStore().setCurrentTid(to.meta.tenant && typeof to.params.tid === 'string' ? to.params.tid : '')
})

// A redeploy replaces the hashed chunk files while a browser may still hold the old
// index.html; every route is lazy, so reload once when a chunk cannot be loaded.
router.onError((error) => {
    if (/dynamically imported module|Importing a module script failed|Failed to fetch/i.test(String(error))) {
        window.location.reload()
    }
})

export default router
